import { useMemo, useState } from "react";
import {
  CircleAlert,
  Edit3,
  FileCheck2,
  FileQuestion,
  FolderPlus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { EmptyState, Field, StatusPill } from "../components/Ui";
import { blankEvidence } from "../lib/projectStore";
import type {
  DesignRisk,
  EvidenceItem,
  EvidenceStatus,
  EvidenceType,
} from "../types/domain";

interface EvidenceProps {
  evidence: EvidenceItem[];
  risks: DesignRisk[];
  onSave: (evidence: EvidenceItem) => void;
  onRemove: (evidenceId: string) => void;
}

export function EvidenceView({ evidence, risks, onSave, onRemove }: EvidenceProps) {
  const [draft, setDraft] = useState<EvidenceItem | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<"all" | EvidenceStatus>("all");

  const visible = useMemo(
    () =>
      evidence.filter(
        (item) =>
          (status === "all" || item.status === status) &&
          `${item.title} ${item.reference} ${item.owner}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      ),
    [evidence, query, status],
  );

  const statusTone = (value: EvidenceStatus) =>
    value === "verified"
      ? "success"
      : value === "received"
        ? "warning"
        : value === "missing"
          ? "danger"
          : "neutral";

  return (
    <>
      <section className="toolbar evidence-toolbar">
        <div className="search-box">
          <Search size={17} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search evidence, reference or owner"
          />
        </div>
        <select
          className="toolbar-select"
          value={status}
          onChange={(event) => setStatus(event.target.value as "all" | EvidenceStatus)}
        >
          <option value="all">All statuses</option>
          <option value="missing">Missing</option>
          <option value="requested">Requested</option>
          <option value="received">Received</option>
          <option value="verified">Verified</option>
        </select>
        <button className="button button-primary" onClick={() => setDraft(blankEvidence())}>
          <FolderPlus size={18} />
          Add evidence
        </button>
      </section>

      {evidence.length === 0 ? (
        <EmptyState
          icon={<FileQuestion size={30} />}
          title="The evidence file is empty"
          action={
            <button className="button button-primary" onClick={() => setDraft(blankEvidence())}>
              <FolderPlus size={18} /> Add the first record
            </button>
          }
        >
          Add controlled references for appointments, drawings, specifications, plans and project
          records. Upload revision-controlled files in Revisions &amp; files.
        </EmptyState>
      ) : (
        <section className="evidence-table-wrap">
          <table className="evidence-table">
            <thead>
              <tr>
                <th>Evidence record</th>
                <th>Type</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Linked risks</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => {
                const linkedRisks = risks.filter((risk) => risk.evidenceIds.includes(item.id));
                return (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.title || "Untitled evidence"}</strong>
                      <small>
                        {item.reference || "No reference"} · {item.version || "No version"}
                      </small>
                    </td>
                    <td>
                      <span className="type-tag">{item.type.replace("-", " ")}</span>
                    </td>
                    <td>{item.owner || "Unassigned"}</td>
                    <td>
                      <StatusPill status={statusTone(item.status)}>{item.status}</StatusPill>
                    </td>
                    <td>
                      <span className="linked-count">{linkedRisks.length}</span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="icon-button"
                          aria-label="Edit evidence"
                          onClick={() => setDraft(item)}
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className="icon-button danger"
                          aria-label="Delete evidence"
                          onClick={() => {
                            if (window.confirm("Delete this evidence record?")) onRemove(item.id);
                          }}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {visible.length === 0 && (
            <div className="table-empty">No evidence records match the current filters.</div>
          )}
        </section>
      )}

      <section className="evidence-guidance">
        <CircleAlert size={20} />
        <div>
          <strong>Evidence graph rule</strong>
          <p>
            Verification is meaningful only when the evidence is linked to the duty, risk, HIRARC
            row or critical control it supports. Uploaded project files are managed in Revisions
            &amp; files and referenced here by controlled identifiers.
          </p>
        </div>
      </section>

      {draft && (
        <div className="modal-backdrop">
          <section className="modal-card modal-compact" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <span className="eyebrow">Controlled reference</span>
                <h2>{evidence.some((item) => item.id === draft.id) ? "Edit evidence" : "Add evidence"}</h2>
              </div>
              <button className="icon-button" onClick={() => setDraft(null)} aria-label="Close">
                <X size={21} />
              </button>
            </div>
            <div className="modal-scroll">
              <div className="form-grid">
                <Field label="Evidence title" wide>
                  <input
                    value={draft.title}
                    onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    placeholder="Use the expected evidence title where possible"
                  />
                </Field>
                <Field label="Evidence type">
                  <select
                    value={draft.type}
                    onChange={(event) =>
                      setDraft({ ...draft, type: event.target.value as EvidenceType })
                    }
                  >
                    <option value="appointment">Appointment</option>
                    <option value="drawing">Drawing</option>
                    <option value="specification">Specification</option>
                    <option value="risk-assessment">Risk assessment</option>
                    <option value="plan">Plan</option>
                    <option value="record">Record</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
                <Field label="Assurance status">
                  <select
                    value={draft.status}
                    onChange={(event) =>
                      setDraft({ ...draft, status: event.target.value as EvidenceStatus })
                    }
                  >
                    <option value="missing">Missing</option>
                    <option value="requested">Requested</option>
                    <option value="received">Received</option>
                    <option value="verified">Verified</option>
                  </select>
                </Field>
                <Field label="Document reference">
                  <input
                    value={draft.reference}
                    onChange={(event) => setDraft({ ...draft, reference: event.target.value })}
                  />
                </Field>
                <Field label="Version or revision">
                  <input
                    value={draft.version}
                    onChange={(event) => setDraft({ ...draft, version: event.target.value })}
                  />
                </Field>
                <Field label="Evidence owner">
                  <input
                    value={draft.owner}
                    onChange={(event) => setDraft({ ...draft, owner: event.target.value })}
                  />
                </Field>
                <Field label="Review date">
                  <input
                    type="date"
                    value={draft.reviewDate}
                    onChange={(event) => setDraft({ ...draft, reviewDate: event.target.value })}
                  />
                </Field>
                <Field label="Verification notes and limitations" wide>
                  <textarea
                    rows={4}
                    value={draft.notes}
                    onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                  />
                </Field>
              </div>
            </div>
            <div className="modal-actions">
              <div className="quality-summary">
                {draft.title && draft.owner ? (
                  <>
                    <FileCheck2 size={17} /> Core metadata recorded
                  </>
                ) : (
                  <>
                    <CircleAlert size={17} /> Title and owner are required
                  </>
                )}
              </div>
              <button className="button button-secondary" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <button
                className="button button-primary"
                disabled={!draft.title.trim() || !draft.owner.trim()}
                onClick={() => {
                  onSave(draft);
                  setDraft(null);
                }}
              >
                Save evidence
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
